import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { serverScheduleUrl, siteScheduleUrl } from '../../schedule/utils/ScheduleUtils';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { IExtractTaskInfo } from '../../ts/models/IExtractTaskInfo';
import { ISubscriptionInfo } from '../../ts/models/ISubscriptionInfo';
import { IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { ExtractTaskType } from '../../ts/ServerApi/types/IExtractTask';
import { ISiteNameWithId } from '../../ts/ServerApi/types/ISiteNameWithId';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ServerUrls } from '../ServerUrls';

export module ExtractTasksGrid {

  export function columns(
    translate: IReactTranslateService,
    scope: DataGridCells.ISelectedItemsScope<IExtractTaskInfo>,
    anyActionsAllowed: boolean,
    actions: IActionButton<IExtractTaskInfo, {}>[],
    getActionTarget: () => IActionTarget<IExtractTaskInfo, {}>,
    isServerPage: boolean,
    serverUrls: ServerUrls,
    siteUrls: SiteUrls,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    showWorkbookOrDatasourceColumn: boolean,
    showScheduleColumn: boolean,
    showSiteColumn: boolean,
    showPriorityColumn: boolean,
    isServerOrSiteAdmin: boolean,
  ) : DataGrid.Column[] {

    const columns: DataGrid.Column[] = [
      CommonDataGridColumns.selected(scope)
    ];

    if (showWorkbookOrDatasourceColumn) {
      columns.push(CommonDataGridColumns.withExtraSpace({
        name: 'workbook-datasource-col',
        dataKey: 'targetName',
        direction: 'asc',
        headerText: translate('column_task_workbook_data_source'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: targetLinkFormatter(siteUrls, translate)
      }));
    }

    columns.push({
      name: 'task-type-col',
      dataKey: 'type',
      direction: 'asc',
      headerText: translate('column_task_type'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCellWithTransform('type',
        (type: string) => type === ExtractTaskType.IncrementExtract
          ? translate('extractTasksIncremental')
          : translate('extractTasksFull'))
    });

    if (showScheduleColumn) {
      columns.push({
        name: 'schedule-name-col',
        dataKey: 'scheduleName',
        direction: 'asc',
        headerText: translate('column_task_schedule'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.ScheduleDescriptionCell(
          isServerOrSiteAdmin
            ? (row: DataGrid.Row) => {
                const subscription = <ISubscriptionInfo>row.data;
                const schedule = subscription.schedule;

                return isServerPage
                  ? serverScheduleUrl(serverUrls, schedule.id, schedule.scheduledAction)
                  : siteScheduleUrl(siteUrls, schedule.id, schedule.scheduledAction);
              }
            : null,
          scheduleFrequencyDescriptionFilter)
      });
    }

    if(showPriorityColumn) {
      columns.push({
        name: 'priority-col',
        dataKey: 'priority',
        direction: 'asc',
        headerText: translate('column_task_priority'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.LocalizedCount('priority')
      });
    }

    if (showSiteColumn) {
      columns.push({
        name: 'site-name-col',
        dataKey: 'siteName',
        direction: 'asc',
        headerText: translate('column_task_site'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('site',
          (site: ISiteNameWithId) => site.name)
      });
    }

    columns.push({
      name: 'last-run-at-col',
      dataKey: 'lastRunAt',
      headerText: translate('column_task_last_update'),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('lastRunAt', translate('never'))
    });

    columns.push({
      name: 'next-run-at-col',
      dataKey: 'runNextAt',
      headerText: translate('column_task_next_update'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('runNextAt', translate('disabled'))
    });

    // Add the actions menu to whichever column was first (not including the 'selected' column)
    if (anyActionsAllowed) {
      columns[1] = CommonDataGridColumns.withActions(columns[1], {
        actions: actions,
        getActionTarget: getActionTarget,
        translate: translate
      }, scope);
    }

    return columns;
  }

  function targetLinkFormatter(siteUrls: SiteUrls, translate: IReactTranslateService) {

    return DataGridCells.Link((row: DataGrid.Row) => {
      const extractTask = <IExtractTaskInfo>row.data;
      const siteUrlsForRow = siteUrls.forSiteName(extractTask.site.urlName);
      if (row.data.datasource) {
        const datasource: IDataSourceInfo = row.data.datasource;
        return {
          text: datasource.name,
          url: siteUrlsForRow.datasourceUrl(datasource.id),
          iconClassName: 'tb-grid-icon-datasource',
          iconTitle: translate('datasource')
        };
      } else {
        const workbook: IWorkbookInfo = row.data.workbook;
        return {
          text: workbook.name,
          url: siteUrlsForRow.workbookUrl(workbook.id),
          iconClassName: 'tb-grid-icon-workbook',
          iconTitle: translate('workbook')
        };
      }
    });
  }

}
